// src/lib/scores/guarantee.ts
//
// Pure score-guarantee math (roadmap 3.1). DB-free + testable. The DB/API layer
// supplies the baseline (our first measured score) and the student's official
// CB score; this decides where they stand against the promise.
//
// POLICY DEFAULTS — review before marketing these publicly:
//   - We promise at least +GUARANTEE_PROMISED_GAIN points over a student's
//     baseline. If a paid student who put in the work (see GUARANTEE_MIN_*
//     below, enforced in the API/admin layer, NOT here) falls short, they can
//     claim a free month.
//   - This module only does the SCORE comparison. Whether someone is paid /
//     active enough to CLAIM is layered on top (claimEligible in the API) so the
//     money-relevant conditions stay explicit and reviewable.

/** Minimum guaranteed improvement over baseline (points). */
export const GUARANTEE_PROMISED_GAIN = 100

/**
 * Minimum practice (drill/practice questions answered) before a shortfall can
 * be CLAIMED — so the guarantee rewards effort, not no-shows. Enforced in the
 * API/admin layer, surfaced here as a constant for one source of truth.
 */
export const GUARANTEE_MIN_PRACTICE_QUESTIONS = 500

export type ScoreGuaranteeState =
  | 'no-baseline' // we haven't measured a starting point yet
  | 'no-official' // baseline exists, but no official score entered
  | 'met' // official − baseline ≥ promised gain 🎉
  | 'short' // official entered but gain < promised

export interface ScoreGuaranteeStatus {
  state: ScoreGuaranteeState
  baseline: number | null
  official: number | null
  /** official − baseline, or null when either is missing. */
  gain: number | null
  promisedGain: number
  /** promisedGain − gain when short (≥1), else 0. */
  shortfall: number
}

/**
 * Compare a student's official score to our baseline against the promise.
 * Pure: no notion of paid/activity — that's layered on for claim eligibility.
 */
export function computeScoreGuarantee(
  baseline: number | null,
  official: number | null,
  promisedGain: number = GUARANTEE_PROMISED_GAIN,
): ScoreGuaranteeStatus {
  if (baseline == null) {
    return {
      state: 'no-baseline',
      baseline: null,
      official,
      gain: null,
      promisedGain,
      shortfall: 0,
    }
  }
  if (official == null) {
    return {
      state: 'no-official',
      baseline,
      official: null,
      gain: null,
      promisedGain,
      shortfall: 0,
    }
  }
  const gain = official - baseline
  if (gain >= promisedGain) {
    return { state: 'met', baseline, official, gain, promisedGain, shortfall: 0 }
  }
  return {
    state: 'short',
    baseline,
    official,
    gain,
    promisedGain,
    shortfall: promisedGain - gain,
  }
}

/**
 * Whether a shortfall can actually be claimed: the score fell short AND the
 * student paid AND put in the minimum work. Kept here (taking primitives) so the
 * full rule is in one tested place; the API supplies isPaid + practiceQuestions.
 */
export function isClaimEligible(
  status: ScoreGuaranteeStatus,
  opts: { isPaid: boolean; practiceQuestions: number },
): boolean {
  return (
    status.state === 'short' &&
    opts.isPaid &&
    opts.practiceQuestions >= GUARANTEE_MIN_PRACTICE_QUESTIONS
  )
}
